import {
	BulkOperationParams,
	BulkOperationType,
	getOperationType,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/bulk-action-utils.ts';
import {
	getFinalSelectionCount,
	getTableAllSelected,
} from '@dealstack/frontend/shared/components/Table/MantineTable/table-utils.ts';
import { TableOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting.ts';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme.ts';
import { GetPersonSecurityDataRes, SecurityFilterDto } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { Table } from '@tanstack/react-table';
import { MutableRefObject } from 'react';
import {
	BulkAction,
	BulkActionGroup,
} from '@dealstack/frontend/shared/components/Table/MantineTable/ActionsButton/ActionsButton.types';

export const createBulkActions = (
	table: Table<GetPersonSecurityDataRes>,
	{
		isAllSelected,
		isLoading,
		hasExportViewerPermission,
		userIsAdmin,
		selectedEntityId,
		tCapManage,
		downloadExcel,
		handleBulkDelete,
		dataOperationsRef,
		showUpdateCurrencyDrawerButton = false,
		setShowUpdateCurrencyDrawer,
		filterDate,
	}: {
		isAllSelected: boolean;
		isLoading: boolean;
		hasExportViewerPermission: boolean;
		hasEditorPermission: boolean;
		userIsAdmin: boolean;
		selectedEntityId: string;
		tCapManage: (key: string) => string;
		downloadExcel: (params: {
			operationType: BulkOperationType;
			selectedEntityId: string;
			table: Table<GetPersonSecurityDataRes>;
			dataOperations: TableOperations<SecurityFilterDto>;
			filterDate?: Date | null;
		}) => Promise<void>;
		handleBulkDelete: (
			payload: BulkOperationParams<
				GetPersonSecurityDataRes,
				TableOperations<SecurityFilterDto>
			>
		) => void;
		dataOperationsRef: MutableRefObject<TableOperations<SecurityFilterDto>>;
		showUpdateCurrencyDrawerButton?: boolean;
		setShowUpdateCurrencyDrawer?: (show: boolean) => void;
		filterDate?: Date | null;
	}
): BulkActionGroup<GetPersonSecurityDataRes> => {
	const specialPrimary: BulkAction<GetPersonSecurityDataRes>[] = [];
	const primary: BulkAction<GetPersonSecurityDataRes>[] = [];
	const special: BulkAction<GetPersonSecurityDataRes>[] = [];
	const secondary: BulkAction<GetPersonSecurityDataRes>[] = [];

	if (showUpdateCurrencyDrawerButton) {
		specialPrimary.push({
			key: 'update-currency',
			label: tCapManage('securities.header_actions.update_currency'),
			disabled: isLoading,
			onExecute: () => {
				return setShowUpdateCurrencyDrawer?.(true);
			},
			executeImmediately: true,
		});
	}

	if (hasExportViewerPermission) {
		primary.push({
			key: 'download',
			label: tCapManage('table.bulk_actions.download_selection'),
			buttonLabel: () =>
				getTableAllSelected(table)
					? `${tCapManage('table.bulk_actions.download_filtered')} (${getFinalSelectionCount(table)})`
					: tCapManage('table.bulk_actions.download_selection'),
			disabled: isLoading,
			dataTestId: getTableAllSelected(table)
				? TEST_ELEMENT_IDS.CAP_SECURITIES_DOWNLOAD_FILTERED
				: TEST_ELEMENT_IDS.CAP_SECURITIES_DOWNLOAD_SELECTED,
			onExecute: () => {
				return downloadExcel({
					operationType: getOperationType(table),
					selectedEntityId,
					table,
					dataOperations: dataOperationsRef.current,
					filterDate,
				});
			},
		});
	}

	if (userIsAdmin) {
		secondary.push({
			key: 'delete',
			label: tCapManage(
				`table.bulk_actions.${isAllSelected ? 'delete_filtered' : 'delete_selection'}`
			),
			color: defaultTheme.v3.colors.red[6],
			disabled: isLoading,
			onExecute: () => {
				handleBulkDelete({
					operationType: getOperationType(table),
					selectedEntityId,
					table,
					dataOperations: dataOperationsRef.current,
				});
			},
		});
	}

	return { specialPrimary, primary, secondary, special };
};
